import { useRoute, useRouter } from 'vue-router/composables';
import { createGlobalState } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { getViewParamsByRoute } from 'library-@/utils/get-params';
import { EXTENDS_PARAM_NAME_FOR_PATENT_FAMILY } from '@/subview_plugins/patent-family/constants';
import _ from 'lodash';

const COMPARE_PATH = 'compare';
const CLMS_PATH = 'clms';
const DESC_PATH = 'desc';

export enum ERouterMode {
    DEFAULT_ID= 'default',
    CUSTOM_ID = 'custom',
    EDIT_PATENT_ID = 'editPatent',
    TEXT_ID = 'text',
    EDIT_TEXT_ID = 'editText',
    PATENT_ID = 'patentId'
}

const ALL_COMPARE_PATH_LEFT = [
    ERouterMode.DEFAULT_ID,
    ERouterMode.CUSTOM_ID,
    ERouterMode.EDIT_PATENT_ID,
    ERouterMode.TEXT_ID,
    ERouterMode.EDIT_TEXT_ID,
];

export const useCompareUrlState = createGlobalState(() => {
    const route = useRoute();
    const router = useRouter();

    const viewParams = computed(() => {
        return getViewParamsByRoute(route, {
            /* 忽略 patentFamily 的扩展 query 参数*/
            omits: [EXTENDS_PARAM_NAME_FOR_PATENT_FAMILY],
            /* 将 pf 转为 _familyType 接口需要*/
            trans: [],
        });
    });

    const isCompareView = computed(() => viewParams.value.isCompareView);

    const compareId = computed(() => {
        return route.query.compareId as string;
    });

    const rows = computed(() => {
        return route.query.rows ? Number(route.query.rows) : 20;
    });

    const page = computed(() => {
        return route.query.page ? Number(route.query.page) : 1;
    });

    const sort = computed(() => {
        return route.query.sort as string;
    });

    const _type = computed(() => {
        return route.query._type as string;
    });

    const leftId = computed(() => {
        return route.params.leftId as string;
    });

    const rightId = computed(() => {
        return route.params.rightId as string;
    });

    const leftPath = computed(() => {
        return route.params.left as string;
    });

    // 右侧对比栏无专利选中的状态
    const isTargetEmpty = computed(() => {
        return rightId.value === ERouterMode.DEFAULT_ID;
    });

    // srp勾选专利，进入该模式，此时左侧主体栏需要用户手动选择对比内容
    const isSourceCustom = computed(() => {
        return leftId.value === ERouterMode.CUSTOM_ID;
    });

    // 左侧是专利对比的模式
    const isSourcePatent = computed(() => {
        return leftPath.value !== COMPARE_PATH;
    });

    // 左边是编辑专利的模式
    const isSourceEditPatent = computed(() => {
        return leftId.value === ERouterMode.EDIT_PATENT_ID;
    });

    // 左边是自编辑的文本对比模式
    const isSourceCustomText = computed(() => {
        return leftId.value === ERouterMode.TEXT_ID;
    });

    // 语义检索过来的文本模式，此时需要执行compare init
    const isSourceCompareText = computed(() => {
        return leftId.value === ERouterMode.DEFAULT_ID;
    });

    // 左边是编辑文本模式
    const isSourceEditText = computed(() => {
        return leftId.value === ERouterMode.EDIT_TEXT_ID;
    });

    const isSourceInTextTab = computed(() => {
        return leftPath.value === CLMS_PATH || leftPath.value === DESC_PATH;
    });

    const prevCompareMode = ref('');
    const prevComparePath = ref('');

    watch(route, () => {
        if (!isCompareView.value) return;
        if (isSourcePatent.value || isSourceCustomText.value || isSourceCompareText.value) {
            prevCompareMode.value = leftId.value;
            if (isSourcePatent.value) {
                prevComparePath.value = leftPath.value;
            }
        }
    });

    const changeCompareMode = (mode:string, path?:string) => {
        const _mode = mode || ERouterMode.CUSTOM_ID;
        const params:{[key: string]: string;} = {
            ...route.params,
            leftId: _mode,
        };
        // leftId不在枚举值中，则说明是patentId,此时需要跳转到专利tab中
        if (!_.includes(ALL_COMPARE_PATH_LEFT, _mode)) {
            params.left = path || prevComparePath.value;
            // 如果在枚举值中，且左侧路径不是compare，需要切换到compare
        } else if (route.params.left !== COMPARE_PATH) {
            params.left = COMPARE_PATH;
        }
        router.push({
            query: route.query,
            params,
        });
    };

    return {
        isCompareView,
        compareId,
        rows,
        page,
        sort,
        _type,
        leftId,
        rightId,
        leftPath,

        isTargetEmpty,
        isSourceCustom,
        isSourcePatent,
        isSourceCompareText,
        isSourceEditPatent,
        isSourceCustomText,
        isSourceEditText,
        isSourceInTextTab,

        prevCompareMode,

        changeCompareMode,
    };
});
